/**
 * @voltxampere/presets — RLC series resonance (#6)
 *
 * V1 (DC) → R → L → C → gnd. At DC the capacitor is open so no
 * current flows; both V(L_node) and V(out) sit at the source rail
 * minus zero IR drop, i.e. V(out) = V1. Sprint 22+ AC analysis
 * will surface the resonant peak at fr = 1/(2π√LC). The DC test
 * pins the no-current invariant via V(out) = V1.
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
import { makeCircuit, makeNet, makeComponent } from "../_builders.js";
// Sprint 25 R2 lane 4D-A — hand-authored layout. Source, R, L
// laid out horizontally on a single rail; capacitor rotated 90° at
// the right end so its bottom terminal drops cleanly to ground.
// 160-px column spacing keeps the chain readable on the canvas.
export const rlcSeriesResonance = {
    id: "rlc-series-resonance",
    name: { tr: "RLC Seri Rezonans", en: "RLC Series Resonance" },
    category: "filter",
    difficulty: 3,
    description: {
        tr: "Seri RLC devresi. R=10Ω, L=100µH, C=1nF → fr ≈ 503kHz. DC'de kapasitör açıktır.",
        en: "Series RLC circuit. R=10Ω, L=100µH, C=1nF → fr ≈ 503kHz. Cap is open at DC.",
    },
    circuit: makeCircuit({
        slot: 6,
        name: "RLC Series Resonance",
        description: "Series RLC bandpass — resonant at 503 kHz",
        nets: [
            makeNet({ name: "gnd", isGround: true }),
            makeNet({ name: "in" }),
            makeNet({ name: "lc_mid" }),
            makeNet({ name: "out" }),
        ],
        components: [
            makeComponent({
                id: "V1",
                type: "voltage-source",
                params: { voltage: 1 },
                terminals: { pos: "net_in", neg: "net_gnd" },
                label: "V1",
                position: { x: -240, y: 0 },
                rotation: 0,
            }),
            makeComponent({
                id: "R1",
                type: "resistor",
                params: { resistance: 10 },
                terminals: { p1: "net_in", p2: "net_lc_mid" },
                label: "R1",
                position: { x: -80, y: 0 },
                rotation: 0,
            }),
            makeComponent({
                id: "L1",
                type: "inductor",
                params: { inductance: 100e-6 },
                terminals: { p1: "net_lc_mid", p2: "net_out" },
                label: "L1",
                position: { x: 80, y: 0 },
                rotation: 0,
            }),
            makeComponent({
                id: "C1",
                type: "capacitor",
                params: { capacitance: 1e-9 },
                terminals: { p1: "net_out", p2: "net_gnd" },
                label: "C1",
                position: { x: 240, y: 0 },
                rotation: 90,
            }),
            makeComponent({
                id: "GND1",
                type: "ground",
                params: {},
                terminals: { p: "net_gnd" },
                position: { x: -240, y: 80 },
                rotation: 0,
            }),
        ],
        wires: [
            // in rail: V1.pos (-240, -10) → R1.p1 (-100, 0)
            {
                id: "w_in",
                netId: "net_in",
                path: [
                    { x: -240, y: -10 },
                    { x: -240, y: -40 },
                    { x: -100, y: -40 },
                    { x: -100, y: 0 },
                ],
                style: "orthogonal",
            },
            // lc_mid rail: R1.p2 (-60, 0) → L1.p1 (60, 0)
            {
                id: "w_lc_mid",
                netId: "net_lc_mid",
                path: [
                    { x: -60, y: 0 },
                    { x: 60, y: 0 },
                ],
                style: "orthogonal",
            },
            // out rail: L1.p2 (100, 0) → C1.p1 (240, -20)
            {
                id: "w_out",
                netId: "net_out",
                path: [
                    { x: 100, y: 0 },
                    { x: 240, y: 0 },
                    { x: 240, y: -20 },
                ],
                style: "orthogonal",
            },
            // gnd leg A: V1.neg (-240, 10) → GND.p (-240, 71)
            {
                id: "w_gnd_left",
                netId: "net_gnd",
                path: [
                    { x: -240, y: 10 },
                    { x: -240, y: 71 },
                ],
                style: "orthogonal",
            },
            // gnd leg B: C1.p2 (240, 20) → GND.p (-240, 71)
            {
                id: "w_gnd_right",
                netId: "net_gnd",
                path: [
                    { x: 240, y: 20 },
                    { x: 240, y: 110 },
                    { x: -240, y: 110 },
                    { x: -240, y: 71 },
                ],
                style: "orthogonal",
            },
        ],
    }),
    expectedValues: [
        {
            node: "out",
            analysis: "dc",
            value: 1,
            tolerance: 0.001,
            unit: "V",
        },
    ],
    learningGoals: [
        {
            tr: "Rezonans frekansı fr = 1/(2π√LC) — empedans en küçük değer alır.",
            en: "Resonant frequency fr = 1/(2π√LC) — impedance is minimised.",
        },
        {
            tr: "Q faktörü = (1/R)·√(L/C) seçicilik (bant genişliği) ile ters orantılıdır.",
            en: "Q factor = (1/R)·√(L/C) — inversely related to bandwidth (selectivity).",
        },
        {
            tr: "DC'de C açık, L kısa: bütün kaynak gerilimi C üzerine düşer.",
            en: "At DC: C is open, L is short — full source voltage drops across C.",
        },
    ],
    references: ["Horowitz/Hill, The Art of Electronics, Ch. 1"],
};
//# sourceMappingURL=rlc-series-resonance.js.map